import {course_getCourses_Api, curriculum_getByOwner_Api} from 'apis'
import {getId, getType} from 'apis/model/base'
import {Skeleton} from 'antd'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import CustomLazyPaginationWrapper from 'modules/asyncCache/components/CustomLazyPaginationWrapper'
import React, {useContext} from 'react'
import {Link} from 'react-router-dom'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import {renderIf} from 'views/Shared'
import OrganizationContainerContext from '../OrganizationContainerContext'
import {HeaderWithIconLine} from '../../Templates/items/ListHeader'
import EmptyHolder from '../../../../components/EmptyHolder'
import {GiGraduateCap} from 'react-icons/gi'
import {FaChalkboardTeacher} from 'react-icons/fa'
import {GoDotFill} from 'react-icons/go'
import {IoCalendar} from 'react-icons/io5'
import {BsFillCalendar2DateFill} from 'react-icons/bs'
import {formatSimplifyDate} from 'helpers/dateUtils'
import getLinkToDetail from 'helpers/getLinkToDetail'
import getTitle from 'helpers/getTitle'
import classNames from 'classnames'

// ─── Status badge ─────────────────────────────────────────────────────────────

const getCourseStatus = (item, t) => {
    const {is_expired, is_register_expired, time_open} = item ?? {}
    if (is_expired) return {label: t('ended'), cls: 'text-color-300 bg-color-50 border-color-100'}
    if (is_register_expired) return {label: t('registration closed'), cls: 'text-orange-700 bg-orange-50 border-orange-200'}
    if (time_open && new Date(time_open) > new Date()) return {label: t('upcoming'), cls: 'text-blue-700 bg-blue-50 border-blue-200'}
    return {label: t('ongoing'), cls: 'text-green-700 bg-green-50 border-green-200'}
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const SectionSkeleton = ({rows = 2}) => (
    <div className="flex flex-col gap-3">
        {Array.from({length: rows}).map((_, i) => (
            <div key={i} className="p-4 rounded-xl border border-color-50 background">
                <Skeleton active paragraph={{rows: 2}} title={{width: '60%'}}/>
            </div>
        ))}
    </div>
)

// ─── Curriculum card ──────────────────────────────────────────────────────────

const CurriculumCard = ({item}) => {
    const t = useTranslate()
    const {title, description, duration_in_months, subjects = []} = item ?? {}

    return (
        <div className="flex background border border-color-50 rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden">
            {/* accent bar */}
            <div className="w-1 flex-shrink-0 bg-blue-400 rounded-l-xl"/>
            <div className="flex-1 px-4 py-3 flex flex-col gap-2 min-w-0">
                <div className="flex items-start justify-between gap-3">
                    <h3 className="font-bold text-base text-color-000 leading-snug flex-1 min-w-0">
                        {title}
                    </h3>
                    {duration_in_months > 0 && (
                        <span className="flex-shrink-0 px-2 py-0.5 border border-blue-200 bg-blue-50 rounded-full text-xs font-medium text-blue-600 whitespace-no-wrap">
                            {`${duration_in_months} ${t('months')}`}
                        </span>
                    )}
                </div>
                {subjects.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                        {subjects.map((s, i) => (
                            <span
                                key={s.id || i}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 border border-blue-100 rounded-md text-xs font-medium text-blue-600">
                                <GoDotFill size={6} className="text-blue-400"/>
                                {s.title}
                                {s.number_of_sessions > 0 && (
                                    <span className="text-blue-400 font-normal">
                                        {`(${s.number_of_sessions}${t('sessions')[0]})`}
                                    </span>
                                )}
                            </span>
                        ))}
                    </div>
                )}
                {renderIf(
                    description,
                    <p className="text-xs text-color-300 tracking-wide leading-relaxed whitespace-pre-line">
                        {description}
                    </p>
                )}
            </div>
        </div>
    )
}

// ─── Course card ──────────────────────────────────────────────────────────────

const CourseCard = ({item}) => {
    const t = useTranslate()
    const {time_open, time_close} = item ?? {}
    const status = getCourseStatus(item, t)

    return (
        <div className="flex background border border-color-50 rounded-xl shadow-sm hover:shadow-md hover:scale-2xs transition-all overflow-hidden">
            {/* accent bar */}
            <div className={classNames(
                'w-1 flex-shrink-0 rounded-l-xl',
                item?.is_expired ? 'bg-color-100' :
                item?.is_register_expired ? 'bg-orange-400' :
                (time_open && new Date(time_open) > new Date()) ? 'bg-blue-400' : 'bg-green-400'
            )}/>
            <div className="flex-1 px-4 py-3 flex flex-col gap-1.5 min-w-0">
                <div className="flex items-start justify-between gap-2">
                    <Link
                        to={getLinkToDetail(item)}
                        className="flex-1 min-w-0 font-bold text-base text-color-000 no-underline leading-snug hover:text-primary max-lines-2">
                        {getTitle(item)}
                    </Link>
                    <span className={classNames(
                        'flex-shrink-0 px-2 py-0.5 text-xs font-semibold rounded-full border whitespace-no-wrap mt-0.5',
                        status.cls
                    )}>
                        {status.label}
                    </span>
                </div>
                {(time_open || time_close) && (
                    <div className="flex items-center gap-1 text-xs text-color-300">
                        <BsFillCalendar2DateFill size={11} className="text-green-500 flex-shrink-0"/>
                        <span>
                            {time_open && formatSimplifyDate(time_open, 'medium')}
                            {time_close && ` - ${formatSimplifyDate(time_close, 'medium')}`}
                        </span>
                    </div>
                )}
            </div>
        </div>
    )
}

// ─── Curriculum section ───────────────────────────────────────────────────────

const OrganizationCurriculumSection = () => {
    const t = useTranslate()
    const {organization} = useContext(OrganizationContainerContext)
    const owner_id = getId(organization)
    const owner_type = getType(organization)

    return (

        <CustomLazyPaginationWrapper
            alwaysVisible={true}
            id={[curriculum_getByOwner_Api.path, owner_type, owner_id].join('/')}
            apiInfo={curriculum_getByOwner_Api}
            query={bindQueryParams([{type: owner_type}, {id: owner_id}])}
            renderItem={(item, index) => <CurriculumCard key={index} item={item}/>}
            renderLoading={() => <SectionSkeleton rows={2}/>}
            renderWrap={(child, __, count) => {
                if (!count) return null;
                return (
                    <div className="flex flex-col gap-4">
                        <HeaderWithIconLine
                            icon={<GiGraduateCap size={22} className="text-blue-500"/>}
                            renderTitle={() => (
                                <div className="flex flex-col gap-0.5">
                        <span className="font-bold text-base text-color-000 uppercase tracking-wide">
                            {t('curriculum')}
                        </span>

                                </div>
                            )}

                        />
                        {child}
                        <div className="border-t border-color-50"/>
                    </div>
                )
            }}
        />
    )

}

// ─── Course group ─────────────────────────────────────────────────────────────

const OrganizationCourseGroup = ({academic_year, academic_field, owner_id, owner_type}) => {
    const id = [course_getCourses_Api.path, owner_type, owner_id, academic_year, academic_field].join('/')

    return (
        <CustomLazyPaginationWrapper
            id={id}
            apiInfo={course_getCourses_Api}
            query={bindQueryParams([{id: owner_id}, {type: owner_type}])}
            values={{academic_year, academic_field, filter: 1}}
            renderItem={(item) => <CourseCard key={getId(item)} item={item}/>}
            renderLoading={() => <SectionSkeleton rows={2}/>}
            renderWrap={(child, __, count) => count > 0 && (
                <div className="flex flex-col gap-2">{child}</div>
            )}
        />
    )
}

// ─── Courses section ──────────────────────────────────────────────────────────

const OrganizationCoursesSection = () => {
    const t = useTranslate()
    const {organization} = useContext(OrganizationContainerContext)
    const owner_id = getId(organization)
    const owner_type = getType(organization)

    const working_year_field = _.get(
        organization,
        'workspace_settings.courses.groups.working_year_field_groups',
        null
    )

    return (
        <div className="flex flex-col gap-4">
            <HeaderWithIconLine
                icon={<FaChalkboardTeacher size={20} className="text-green-500"/>}
                renderTitle={() => (
                    <div className="flex flex-col gap-0.5">
                        <span className="font-bold text-base text-color-000 uppercase tracking-wide">
                            {t('course')}
                        </span>
                    </div>
                )}
                renderSubTitle={() => (
                    <div className="flex flex-col gap-0.5">
                        <span className="text-xs text-color-300 italic font-normal normal-case">
                            {t('course introduction description')}
                        </span>
                    </div>
                )}
            />
            {!working_year_field ? (
                <EmptyHolder title={t('course')} subTitle={t('No data available')}/>
            ) : (
                <div className="flex flex-col gap-6">
                    {_.orderBy(Object.keys(working_year_field), null, 'desc').map((academic_year, yi) => {
                        const fields = _.get(working_year_field, academic_year, [])
                        if (!fields.length) return null

                        return (
                            <div key={yi} className="flex flex-col gap-3">
                                {Number(academic_year) > 0 && (
                                    <div className="flex items-center gap-2">
                                        <IoCalendar size={14} className="text-green-500 flex-shrink-0"/>
                                        <span className="font-semibold text-xs text-green-700 uppercase tracking-wider">
                                            {`${t('academic year')} ${academic_year}`}
                                        </span>
                                        <div className="flex-1 border-b border-green-100"/>
                                    </div>
                                )}
                                <div className="flex flex-col gap-4">
                                    {fields.map((academic_field, fi) => (
                                        <div key={fi} className="flex flex-col gap-2">
                                            {renderIf(
                                                academic_field,
                                                <span className="font-medium text-xs text-blue-500 uppercase tracking-widest">
                                                    {academic_field}
                                                </span>
                                            )}
                                            <OrganizationCourseGroup
                                                academic_year={academic_year}
                                                academic_field={academic_field}
                                                owner_id={owner_id}
                                                owner_type={owner_type}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const OrganizationCourseList = () => (
    <div className="flex flex-col gap-8 pb-8">
        <OrganizationCurriculumSection/>
        <OrganizationCoursesSection/>
    </div>
)

export default OrganizationCourseList
